<template>
  <BDLayout>
    <div class="transaction">
      <section id="bd-contracts">
        <div class="container">
          <h6 class="fw-bold mb-2">Contracts</h6>

          <!-- Stats cards -->
          <div class="warehouse-inventory-card-wrapper mb-3 bd-fade-in">
            <div class="warehouse-inventory-card">
              <p>Total<br />Contracts</p>
              <strong>{{ stats.totalContracts }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Total<br />Value</p>
              <strong>{{ formatCurrency(stats.totalValue) }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Most Recent<br />Award</p>
              <strong>{{ stats.recentAward || '-' }}</strong>
            </div>
          </div>

          <div class="products-list transaction-products-list" style="margin-top: 0;">
              <div class="d-flex justify-content-between align-items-center mb-2 bd-fade-in">
              <h6 class="fw-bold">Awarded Contracts</h6>
              <div class="position-relative">
                <button class="btn btn-sm btn-style-2 bd-btn-scale" @click="showFilters = !showFilters">Filters</button>
                <div v-if="showFilters" class="filter-popover card p-3">
                  <div class="mb-2">
                    <label class="form-label">Search</label>
                    <input v-model="filters.search" class="form-control form-control-sm" placeholder="Title..." />
                  </div>
                  <div class="mb-2">
                    <label class="form-label">Client</label>
                    <select v-model="filters.client" class="form-control form-control-sm">
                      <option value="">All</option>
                      <option v-for="c in clientOptions" :key="c" :value="c">{{ c }}</option>
                    </select>
                  </div>
                  <div class="row g-2">
                    <div class="col-6">
                      <label class="form-label">Start Date</label>
                      <input type="date" v-model="filters.startDate" class="form-control form-control-sm" />
                    </div>
                    <div class="col-6">
                      <label class="form-label">End Date</label>
                      <input type="date" v-model="filters.endDate" class="form-control form-control-sm" />
                    </div>
                  </div>
                  <div class="mb-2 mt-2">
                    <label class="form-label">Min Value</label>
                    <input type="number" v-model="filters.minValue" class="form-control form-control-sm" placeholder="0" />
                  </div>
                    <div class="d-flex justify-content-between mt-2">
                    <button class="btn btn-sm btn-secondary bd-btn-scale" @click="resetFilters">Reset</button>
                    <button class="btn btn-sm btn-primary bd-btn-scale" @click="applyFilters">Filter</button>
                  </div>
                </div>
              </div>
            </div>

              <SkeletonLoader v-if="loading" type="table" :rows="6" :columns="6"></SkeletonLoader>

              <table
                v-else
                id="contractsTable"
                ref="contractsTable"
                class="table table-sm table-hover mb-0"
              >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Opportunity</th>
                  <th>Client</th>
                  <th>Country</th>
                  <th>Award Date</th>
                  <th>Value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, idx) in contracts" :key="c.id">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ c.opportunity?.tenderTitle || c.opportunityId }}</td>
                  <td>{{ c.opportunity?.client?.name || c.opportunity?.clientName || c.client?.name || c.clientName || '-' }}</td>
                  <td>{{ c.opportunity?.country?.name || c.opportunity?.countryName || c.client?.country || c.client?.countryName || c.country || '-' }}</td>
                  <td>{{ formatDate(c.awardDate) }}</td>
                  <td>{{ formatCurrency(c.contractValue) }}</td>
                  <td>
                                <button class="btn btn-sm btn-outline-primary" @click.stop="viewContract(c)">
                                  <i class="fas fa-arrow-right"></i>
                                </button>
                  </td>
                </tr>
                <tr v-if="!loading && contracts.length === 0">
                  <td colspan="6" class="text-center text-muted">No awarded contracts found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import $ from 'jquery'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import 'datatables.net-buttons-bs5'
import 'datatables.net-responsive-bs5'
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'

import BDLayout from '@/components/BDLayout.vue'
import SkeletonLoader from '@/components/SkeletonLoader'
import { bidApi } from '@/api/bid'

export default {
  name: 'ContractsPage',
  components: { BDLayout, SkeletonLoader },
  setup() {
    const loading = ref(false)
    const contracts = ref([])
    const originalContracts = ref([])
    const contractsTable = ref(null)
    let dataTable = null

    const stats = reactive({ totalContracts: 0, totalValue: 0, recentAward: '-' })

    // Filters state
    const showFilters = ref(false)
    const filters = reactive({ search: '', client: '', country: '', startDate: '', endDate: '', minValue: '' })
    const clientOptions = ref([])

    // initialize country filter from selector (if previously saved)
    try {
      const _bd = JSON.parse(localStorage.getItem('bd_selected_country') || 'null')
      if (_bd) filters.country = _bd.name || _bd.code || _bd
    } catch (e) { /* ignore localStorage parse errors */ }

    const router = useRouter()

    const load = async () => {
      loading.value = true
      try {
        const list = await bidApi.listContracts({ status: 'AWARDED' })
        const arr = Array.isArray(list) ? list : (list.data || [])
        originalContracts.value = arr
        contracts.value = arr
        // derive client options (try multiple possible shapes returned by backend)
        clientOptions.value = Array.from(new Set(arr.map(c => (
          c.opportunity?.client?.name || c.opportunity?.clientName || c.client?.name || c.clientName || undefined
        )).filter(Boolean)))
        // compute stats
        stats.totalContracts = arr.length
        stats.totalValue = arr.reduce((s, c) => s + (Number(c.contractValue) || 0), 0)
        const recent = arr.map(c => c.awardDate).filter(Boolean).sort().reverse()[0]
        stats.recentAward = recent ? new Date(recent).toLocaleDateString() : '-'
      } catch (e) {
        console.error('Failed loading contracts', e)
        contracts.value = []
      } finally {
        loading.value = false
        // initialize datatable after DOM updated
        setTimeout(initializeDataTable, 50)
      }
    }

    const initializeDataTable = () => {
      try {
        if (!contractsTable.value) return
        if (dataTable) { dataTable.destroy(); dataTable = null }
        dataTable = $(contractsTable.value).DataTable({
          dom: 'Bfrtip',
          responsive: true,
          language: {
            paginate: {
              previous: '<i class="fas fa-chevron-left"></i>',
              next: '<i class="fas fa-chevron-right"></i>',
            },
          },
          buttons: [
            'colvis',
            'pageLength',
            { extend: 'csv', text: 'Export CSV', className: 'btn btn-primary btn-sm' },
            { extend: 'excel', text: 'Export Excel', className: 'btn btn-primary btn-sm' },
            { extend: 'print', text: 'Print', className: 'btn btn-secondary btn-sm' },
          ],
        })
      } catch (err) {
        // non-fatal
        console.warn('DataTable init failed', err)
      }
    }

    const applyFilters = () => {
      const arr = originalContracts.value || []
      const out = arr.filter(c => {
        if (filters.search) {
          const s = filters.search.toLowerCase()
          const inTitle = (c.opportunity?.tenderTitle || '').toLowerCase().includes(s)
          if (!inTitle) return false
        }
        if (filters.country) {
          const countryCandidates = [
            c.opportunity && (c.opportunity.country || c.opportunity.countryName),
            c.client && (c.client.country || c.client.countryName),
            c.opportunity && c.opportunity.client && (c.opportunity.client.country || c.opportunity.client.countryName),
          ].filter(Boolean)
          const found = countryCandidates.some(val => {
            if (!val) return false
            const name = (typeof val === 'string') ? val : (val.name || val)
            return String(name) === String(filters.country)
          })
          if (!found) return false
        }
        if (filters.client) {
          if ((c.opportunity?.client?.name || '') !== filters.client) return false
        }
        if (filters.minValue) {
          const min = Number(filters.minValue) || 0
          if ((Number(c.contractValue) || 0) < min) return false
        }
        if (filters.startDate) {
          if (!c.awardDate) return false
          const d = new Date(c.awardDate)
          if (d < new Date(filters.startDate)) return false
        }
        if (filters.endDate) {
          if (!c.awardDate) return false
          const d = new Date(c.awardDate)
          const end = new Date(filters.endDate)
          end.setHours(23,59,59,999)
          if (d > end) return false
        }
        return true
      })

      contracts.value = out
      // update stats
      stats.totalContracts = out.length
      stats.totalValue = out.reduce((s, c) => s + (Number(c.contractValue) || 0), 0)
      const recent = out.map(c => c.awardDate).filter(Boolean).sort().reverse()[0]
      stats.recentAward = recent ? new Date(recent).toLocaleDateString() : '-'

      // re-init table
      setTimeout(() => { if (dataTable) { dataTable.destroy(); dataTable = null } initializeDataTable() }, 50)
    }

    const resetFilters = () => {
      filters.search = ''
      filters.client = ''
      filters.startDate = ''
      filters.endDate = ''
      filters.minValue = ''
      contracts.value = originalContracts.value || []
      stats.totalContracts = contracts.value.length
      stats.totalValue = contracts.value.reduce((s, c) => s + (Number(c.contractValue) || 0), 0)
      const recent = contracts.value.map(c => c.awardDate).filter(Boolean).sort().reverse()[0]
      stats.recentAward = recent ? new Date(recent).toLocaleDateString() : '-'
      setTimeout(() => { if (dataTable) { dataTable.destroy(); dataTable = null } initializeDataTable() }, 50)
    }

    const viewContract = (c) => {
      if (!c || !c.id) return
      router.push({ name: 'ContractDetail', params: { id: c.id } })
    }

    onMounted(() => { load() })
    onBeforeUnmount(() => { if (dataTable) { dataTable.destroy(); dataTable = null } })

    // respond to BD country changes
    const onBdCountryChanged = (e) => {
      const country = e && e.detail ? e.detail.country : null
      filters.country = country ? (country.name || country.code || country) : ''
      applyFilters()
    }

    // attach listener
    window.addEventListener('bd-country-changed', onBdCountryChanged)
    // ensure cleanup
    onBeforeUnmount(() => {
      try { window.removeEventListener('bd-country-changed', onBdCountryChanged) } catch (e) { /* ignore */ }
      if (dataTable) { dataTable.destroy(); dataTable = null }
    })

    const formatDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString() } catch (_) { return d } }
    const formatCurrency = (v) => { if (v === undefined || v === null) return '-'; return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(v) }

    return { loading, contracts, contractsTable, stats, formatDate, formatCurrency, viewContract, showFilters, filters, applyFilters, resetFilters, clientOptions }
  }
}
</script>

<style scoped>
.warehouse-inventory-card-wrapper { display:flex; gap:1rem; margin-bottom:1rem }
.warehouse-inventory-card { background:#fff; padding:1rem; border-radius:6px; flex:1; box-shadow:0 1px 2px rgba(0,0,0,0.03); display:flex; align-items:center; justify-content:space-between }
.warehouse-inventory-card p { margin:0; color:#6b7280; font-size:0.9rem }
.warehouse-inventory-card strong { font-size:1.25rem; text-align:right }
.transaction-products-list { background: transparent; padding: 15px; border-radius: 0 }

.filter-popover {
  position: absolute;
  right: 0;
  top: 36px;
  width: 260px;
  z-index: 50;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
}
</style>
